import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddDistributorSellInvoice } from "./API/Action";
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  unitCaseEnum,
} from '../../utils/settings';
import dayjs from 'dayjs';
import ProductCard from './components/ProductCard';
import CustomButton from '../../v2/MUI_custom_component/Button/CustomButton';
import StandardChip from '../../v2/MUI_custom_component/chips/StandardChip';
import { trackAnalyticsEvent } from '../../utils/clevertap/clevertapUtils';
import GlassModal from '../../v2/MUI_custom_component/Modal/GlassModal';
import {
  GetAllCityOfState,
  GetAllStates,
} from "../Common/API/Geography/action";

const InventoryCheckout = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const {
    shipping_info = {},
    bill_info = {},
    influencerTabSelected = false,
    selectedRole = '',
  } = state;

  // Track checkout page load
  useEffect(() => {
    trackAnalyticsEvent('INVENTORY_CHECKOUT_PAGE_LOADED', {
      screen_name: 'inventory_checkout',
      has_shipping_info: Object.keys(shipping_info).length > 0,
      has_bill_info: Object.keys(bill_info).length > 0,
      influencer_tab_selected: influencerTabSelected,
      selected_role: selectedRole,
      action: 'page_load'
    });
  }, []);
  const handleClose = () => {
      // Track confirmation modal close/cancel
      trackAnalyticsEvent('CHECKOUT_CONFIRMATION_MODAL_CANCELLED', {
        screen_name: 'inventory_checkout',
        products_count: cartItemList.length,
        total_quantity: calculateTotalQuantity(),
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        has_invoice_number: invoiceNumber.length > 0,
        action: 'confirmation_modal_cancelled'
      });
      
      setOpenConfirmModal(false);
  };

  const [loading, setLoading] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  
  const getStateName = (stateId) => {
    const state = states.find(s => s.value === stateId);
    return state ? state.label : stateId;
  };
  const getDistrictName = (districtId) => {
    const district = districts.find(d => d.value === districtId);
    return district ? district.label : districtId;
  };

  const getStates = async () => {
    const { data, error, message } = await GetAllStates();

    if (!error) {
      setStates(
        data.map((state) => ({
          label: state.state_name,
          value: state.id,
        })),
      );
    } else {
      Sentry.captureMessage('GetAllStates failed', {
        level: 'warning',
        tags: { module: 'inventory', flow: 'checkout' },
        extra: { message },
      });
      return toast.error(message);
    }
  };

  const getDistricts = async (stateId) => {
    setDistricts([]);

    const { data, error, message } = await GetAllCityOfState(stateId);

    if (!error) {
      setDistricts(
        data.map((district) => ({
          label: district.district_name,
          value: district.id,
        })),
      );
    } else {
      Sentry.captureMessage('GetAllCityOfState failed', {
        level: 'warning',
        tags: { module: 'inventory', flow: 'checkout' },
        extra: { stateId, message },
      });
      return toast.error(message);
    }
  };
  useEffect(() => {
      if (bill_info.state_id) getDistricts(bill_info.state_id);
  }, [bill_info.state_id]);
    
  useEffect(() => {
    getStates();
  }, []);

  const onCheckOut = async () => {
      // Track checkout initiation
      trackAnalyticsEvent('INVENTORY_CHECKOUT_INITIATED', {
        screen_name: 'inventory_checkout',
        products_count: cartItemList.length,
        total_quantity: calculateTotalQuantity(),
        uom: getUOM(),
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        influencer_tab_selected: influencerTabSelected,
        delivery_company_id: influencerTabSelected ? bill_info.company_id : shipping_info.company_id,
        action: 'checkout_initiated'
      });

      const newPayload = {
        products: cartItemList.map((prod) => {
          const sellQty = prod.sellToBuyerQty.qty;
          return {
            product_id: prod.product_info.product_id,
            quantity: {
              quantity_in_bags:
                (unitCaseWise === unitCaseEnum['UNIT'] && sellQty) || null,
              quantity_in_metric_ton:
                (unitCaseWise === unitCaseEnum['CASE'] && sellQty) || null,
              multiplier: prod.allocable_quantity.multiplier,
              unit: prod.allocable_quantity.unit,
              base_unit: prod.allocable_quantity.base_unit,
            },
            deliver_to_esi_id :  bill_info.user_esi_id,
            deliver_to: influencerTabSelected ? bill_info.company_id : shipping_info.company_id,
            price_per_unit: 0,
            gst_amount: 0,
            total_amount: 0,
            product_gst_percentage: 0,
          };
        }),
        company_esi_id: company_esi_id,
        invoice_no: invoiceNumber,
        invoice_files_base64: [],
        date_of_invoice: dayjs(invoiceDate).format('YYYY-MM-DD'),
      };
      setLoading(true);
      const { error, message } = await AddDistributorSellInvoice(newPayload);
      setLoading(false);
  
      if (!error) {
        // Track successful checkout
        trackAnalyticsEvent('INVENTORY_CHECKOUT_SUCCESS', {
          screen_name: 'inventory_checkout',
          products_count: cartItemList.length,
          total_quantity: calculateTotalQuantity(),
          uom: getUOM(),
          invoice_number: invoiceNumber,
          invoice_date: invoiceDate,
          action: 'checkout_completed'
        });

        dispatch({ type: 'RESET_FMCG_REDUCER' });
        dispatch({ type: 'CLEAR_RETAILERS' });
        dispatch({ type: 'RESET_BCM_ORDER' });
        return navigate('/inventory/order-completed', { replace: true });
      } else {
        // Track checkout failure
        trackAnalyticsEvent('INVENTORY_CHECKOUT_FAILED', {
          screen_name: 'inventory_checkout',
          error_message: message,
          products_count: cartItemList.length,
          action: 'checkout_failed'
        });
        Sentry.captureMessage('AddDistributorSellInvoice failed', {
          level: 'warning',
          tags: { module: 'inventory', flow: 'checkout' },
          extra: { message, products_count: cartItemList.length, company_esi_id },
        });
        toast.error(message);
      }
  };

  const {
      cartItemList,
      unitCaseWise,
      company_esi_id,
  } = useSelector((state) => {
    return {
      cartItemList: state.fmcgReducer.cartItemList,
      unitCaseWise: state.fmcgReducer.unitCaseWise,
      company_esi_id: state.bcmOrderReducer.company_esi_id,
    };
  });
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  const calculateTotalQuantity = () => {
    const total = cartItemList.reduce((sum, prod) => {
      return sum + (prod?.sellToBuyerQty?.qty || 0);
    }, 0);
    return Math.round((total + Number.EPSILON) * 1000) / 1000;
  };
  const getUOM = () => {
    if (cartItemList.length > 0) {
      return unitCaseWise === unitCaseEnum['UNIT']
        ? cartItemList[0]?.allocable_quantity?.base_unit || 'Units'
        : cartItemList[0]?.allocable_quantity?.unit || 'Cases';
    }
    return 'Units';
  };

  const getQuantityInCasesUnit = (
    quantity_in_metric_ton,
    quantity_in_bags,
    multiplier,
    is_inventory_homogeneous,
    unit,
    base_unit,
  ) => {
    if (is_inventory_homogeneous) {
      return quantity_in_metric_ton;
    } else {
      const cases = Math.floor(quantity_in_bags / multiplier);
      const units = quantity_in_bags % multiplier;
      return `${cases} ${unit} ${units} ${base_unit}`;
    }
  };
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent:'end', 
        p: { xs: 2, md: 0 },
        mr: { xs: 0, md: 4 }, 
        position: {xs: 'sticky', md: 'relative'}, 
        top: 0, 
        zIndex: 1000, 
        boxShadow: {xs: '0px 2px 8px 0px #0000001A', md: 'none'},
        background: {xs: '#FAFAFA', md: 'transparent'}
        }}>
        <CustomButton 
          variant='filled'
          color='primary'
          label={'Confirm & Proceed'} 
          disabled={loading} 
          onClick={()=> {
            // Track done button click
            trackAnalyticsEvent('CHECKOUT_DONE_BUTTON_CLICKED', {
              screen_name: 'inventory_checkout',
              products_count: cartItemList.length,
              total_quantity: calculateTotalQuantity(),
              action: 'done_button_click'
            });
            setOpenConfirmModal(true);
          }}
          sx={{ width : { xs: '100%', sm: 'auto' }}}
        />
      </Box>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
          Checkout
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={influencerTabSelected ? 12 : 6} sx={{ height: '100%' }} alignItems={'stretch'}>
            <Box sx={{ p: 2 , background: '#fff', borderRadius: 2 }}>
              <Typography variant="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
                Billing Info
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  {bill_info?.company_name && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                    {bill_info.company_name}
                    </Typography> 
                  )}
                  {bill_info?.company_phone_no && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {bill_info.company_phone_no}
                    </Typography> 
                  )}
                  {(bill_info?.address_line_1 || bill_info?.address_line_2 || bill_info?.address_line_3 ) && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                    {bill_info?.address_line_1 || ''} {bill_info?.address_line_2 || ''} {bill_info?.address_line_3 || ''} 
                    </Typography> 
                  )}
                  {bill_info?.state_id && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                    {getStateName(bill_info.state_id)}
                    </Typography> 
                  )}
                  {bill_info?.district_id && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {getDistrictName(bill_info.district_id)}
                    </Typography> 
                  )}
                  {bill_info?.company_pincode && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {getDistrictName(bill_info.company_pincode)}
                    </Typography> 
                  )}
                </Box>
                <Box>
                  {selectedRole && (
                    <StandardChip label={selectedRole} selected={true} />
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
          { !influencerTabSelected && (
            <Grid item xs={12} md={influencerTabSelected ? 12 : 6} sx={{ height: '100%' }}>
              <Box sx={{ p: 2 , background: '#fff', borderRadius: 2 }}>
                <Typography variant="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Shipping Address
                </Typography>
                <Box>
                  {(shipping_info?.address_line_1 || shipping_info?.address_line_2 || shipping_info?.address_line_3 ) && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                    {shipping_info?.address_line_1 || ''} {shipping_info?.address_line_2 || ''} {shipping_info?.address_line_3 || ''} 
                    </Typography> 
                  )}
                  {shipping_info?.district_id && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {getDistrictName(shipping_info.district_id)}
                    </Typography> 
                  )}
                  {shipping_info?.state_id && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                    {getStateName(shipping_info.state_id)}
                    </Typography> 
                  )}
                  {shipping_info?.company_pincode && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {getDistrictName(shipping_info.company_pincode)}
                    </Typography> 
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} sx={{ order: { sm: 1, xs: 2 } }}>
            <Box>
              <Typography variant="h2" sx={{ mb: 2 }}>
                Products
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="labelD">
                  Total Quantity: 
                </Typography>
                <Typography variant="labelD">
                  {calculateTotalQuantity()} {getUOM()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="labelD">
                  UOM: 
                </Typography>
                <Typography variant="labelD">
                  {getUOM()}
                </Typography>
              </Box>
              <Box mt={2} display="flex" flexDirection="column" gap={2}
                sx={{
                  maxHeight: { md : cartItemList.length > 2 ? '235px' : 'auto' }, // adjust as needed
                  overflowY: { md: cartItemList.length > 2 ? 'auto' : 'visible' },
                }}
              >
                {cartItemList.map((prod, index) => {
                  const itemInCart = cartItemList.find(
                    (item) =>
                      item.product_info.product_id ===
                      prod.product_info.product_id,
                  );
                  const availableQtyDisplay = unitCaseWise === unitCaseEnum['UNIT']
                    ? `${prod?.allocable_quantity?.quantity_in_bags} ${prod?.allocable_quantity?.base_unit}`
                    : getQuantityInCasesUnit(
                        prod?.allocable_quantity?.quantity_in_metric_ton,
                        prod?.allocable_quantity?.quantity_in_bags,
                        prod?.allocable_quantity?.multiplier,
                        false, // Assuming non-homogeneous for cart items
                        prod?.allocable_quantity?.unit,
                        prod?.allocable_quantity?.base_unit,
                      );

                  return (
                    <Box key={index}>
                      <ProductCard
                        availableQty={null}
                        unverifiedQty={null}
                        productName={prod?.product_info?.product_name || ''}
                        totalQuantity={prod?.sellToBuyerQty?.qty} 
                        imageUrl={prod?.product_info?.product_logo || ''} 
                        showQuantityControls={false}
                        price={prod?.price_info?.mrp ? prod.price_info.mrp : null}
                        badgeText=''
                        showDeleteButton={false}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}  sx={{ order: { sm: 2, xs: 1 } }}>
            <Box sx={{ p: 2 , background: '#fff', borderRadius: 2 }}>
              <Typography variant="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
                Invoice Details
              </Typography>

                <Typography variant="labelD">
                  Invoice Number
                </Typography>
                <TextField
                  variant='standard'
                  fullWidth
                  value={invoiceNumber}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setInvoiceNumber(newValue);
                    
                    // Track invoice number change
                    trackAnalyticsEvent('CHECKOUT_INVOICE_NUMBER_CHANGED', {
                      screen_name: 'inventory_checkout',
                      invoice_number_length: newValue.length,
                      has_invoice_number: newValue.length > 0,
                      action: 'invoice_number_input'
                    });
                  }}
                  sx={{ mb: 2 }}
                  placeholder='Type here'
                />
                <Typography variant="labelD">
                  Invoice Date*
                </Typography>
                <TextField
                  fullWidth
                  variant='standard'
                  type="date"
                  value={invoiceDate}
                  disabled
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setInvoiceDate(newValue);
                    
                    // Track invoice date change
                    trackAnalyticsEvent('CHECKOUT_INVOICE_DATE_CHANGED', {
                      screen_name: 'inventory_checkout',
                      new_date: newValue,
                      is_today: newValue === dayjs().format('DD/MM/YYYY'),
                      action: 'invoice_date_change'
                    });
                  }}
                  inputProps={{
                    max: dayjs().format('DD/MM/YYYY'),
                    min: dayjs().subtract(14, 'day').format('DD/MM/YYYY')
                  }}
                />
                {invoiceDate == dayjs().format('DD/MM/YYYY') && (
                  <Typography variant="p3" sx={{ mt: 1 }}>Today</Typography>  
                )}
                
            </Box>
          </Grid>
        </Grid>
        <GlassModal
          open={openConfirmModal}
          title=""
          subtitle=''
          topMobile="30%"
          showHeader={false}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}>
            <img
              src='https://d2djimvzddgguw.cloudfront.net/humbee-inventory/delivery_truck.png'
              alt='Cart Empty'
              style={{ width: '100%', maxWidth: 180, marginBottom: 20, marginTop: 20 }}
            />
            <Typography variant="h2" sx={{ mb: 2,  textAlign: 'center' }}>
              Are you sure?
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
              This action cannot be undone. Are you sure you want to proceed?
            </Typography>
              <CustomButton 
                variant='filled'
                color='primary'
                label={'Yes, Proceed'} 
                disabled={loading} 
                onClick={onCheckOut}
                sx={{ width : { xs: '100%', sm: 'auto' }, mb: 2}}
              />
              <CustomButton 
                variant='text'
                color='primary'
                label={'Go Back'} 
                disabled={loading} 
                onClick={handleClose}
                sx={{ width : { xs: '100%', sm: 'auto' }, mb: 2}}
              />
          </Box>
        </GlassModal>
      </Box>
    </>
  );
};

export default InventoryCheckout;
